import { useState } from "react";

import { ArrowsInIcon, QuestionIcon, Button, Heading } from "@stacklet/ui";

import { RbacChip } from "app/components/rbac/RbacChip";
import { ROLES } from "app/constants/rbac";

interface Props {
  /** Whether the help panel is open */
  isOpen?: boolean;
  /** Callback when help panel open state changes */
  onToggle?: (open: boolean) => void;
  /** Whether this instance should render only the toggle button */
  buttonOnly?: boolean;
}

export function HelpDrawer({
  isOpen = false,
  onToggle,
  buttonOnly = false,
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = onToggle ? isOpen : internalOpen;
  const handleToggle = (newOpen: boolean) => {
    if (onToggle) {
      onToggle(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  // If buttonOnly is true, only render the toggle button (and hide it when open)
  if (buttonOnly) {
    // Hide the button when the panel is open
    if (open) {
      return null;
    }

    return (
      <Button onPress={() => handleToggle(!open)} variant="plain">
        <QuestionIcon className="h-5 w-5" />
        <div>Show Help</div>
      </Button>
    );
  }

  // Otherwise, render only the content panel
  return (
    <div className="flex flex-col">
      <div className="flex shrink-0 items-center justify-between border-b border-outline-decorative p-lg pt-0">
        <div className="flex items-center gap-xs">
          <QuestionIcon />
          <Heading className="mb-0" level={3}>
            Help
          </Heading>
        </div>
        <Button
          aria-label="close-help-panel"
          onPress={() => handleToggle(false)}
          variant="plain"
        >
          <ArrowsInIcon />
        </Button>
      </div>
      <div className="flex min-h-screen flex-1 flex-col gap-lg overflow-auto p-lg [&::-webkit-scrollbar-thumb:hover]:bg-neutral-black/50 [&::-webkit-scrollbar-thumb]:rounded-xs [&::-webkit-scrollbar-thumb]:bg-neutral-black/30 [&::-webkit-scrollbar-track]:rounded-xs [&::-webkit-scrollbar-track]:bg-neutral-black/10 [&::-webkit-scrollbar]:w-2">
        <div className="text-[1.125rem] font-semibold">Roles &amp; Targets</div>
        {Object.entries(ROLES).map(([role, { description }]) => (
          <div className="flex flex-col gap-xs" key={role}>
            <RbacChip label={role} variant="role" />
            <p className="text-body-medium text-text-primary">
              {description.long}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
